import { useState } from 'react'
import { useWidth, useTip, sector, polar, fitText } from './kit.js'
import ChartTip from './ChartTip.jsx'

/**
 * RadialBars — a few figures as rings that sweep round a dial: the longest sweep (three quarters
 * of a turn) is the largest figure, and a scale of values runs round the outside like a gauge.
 * The dial runs to a round number just above the largest figure, in round steps.
 * Each ring starts at twelve o'clock, where its name and figure are written, so nothing is read
 * from colour. One series is one colour; the largest ring is the darkest.
 *
 * `items` = [{name, value}] (two to eight), in the order to draw them from the outside in.
 */
const SWEEP = Math.PI * 1.5

/** A round top for the dial and its step: 61 -> 80 in steps of 20, 4.6 -> 5 in steps of 1. */
function dial(max) {
  if (!(max > 0)) return { top: 1, step: 0.25 }
  const raw = max / 4
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(st => st >= raw)
  return { top: Math.ceil(max / step - 1e-9) * step, step }
}

export default function RadialBars({ items, color, fmt }) {
  const [box, W] = useWidth()
  const tip = useTip()
  const [hover, setHover] = useState(null)
  const max = Math.max(...items.map(i => Number(i.value) || 0))
  const R = Math.min(170, Math.max(110, W / 2 - 40))
  const S = 2 * R + 70
  const cx = W / 2, cy = R + 40
  const inner = R * 0.3
  const band = (R - inner) / items.length
  const thick = Math.max(8, Math.min(22, band * 0.68))
  const { top, step } = dial(max)
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, k) => (k * step) / top)
  const show = (e, it) => tip.show(e, (
    <div>
      <p className="font-semibold text-gray-900">{it.name}</p>
      <p className="mt-1 text-gray-700"><span className="font-semibold tabular-nums">{fmt(it.value)}</span>
        {max > 0 && <span className="text-gray-500"> · {Math.round((it.value / max) * 100)}% of the largest</span>}</p>
    </div>
  ))

  return (
    <div ref={box}>
      <div ref={tip.ref} className="relative" onMouseLeave={() => { tip.hide(); setHover(null) }}>
        {W > 0 && (
          <svg width={W} height={S} role="img" aria-label="Radial bars: each ring sweeps as far as its figure" className="pt-spin overflow-visible" style={{ transformOrigin: `${cx}px ${cy}px` }}>
            {/* The dial: a scale round the outside, its numbers in small badges. */}
            <path d={sector(cx, cy, R + 8, R + 9.5, 0, SWEEP)} fill="#D1D5DB" />
            {ticks.map(t => {
              const a = t * SWEEP
              const [x0, y0] = polar(cx, cy, R + 6, a), [x1, y1] = polar(cx, cy, R + 13, a)
              const [bx, by] = polar(cx, cy, R + 28, a)
              const label = fmt(Math.round(top * t * 1000) / 1000)
              const w = Math.max(24, label.length * 6.4 + 12)
              return (
                <g key={t}>
                  <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#9CA3AF" strokeWidth="1.5" />
                  <rect x={bx - w / 2} y={by - 9} width={w} height={18} rx={9} fill="#F3F4F6" stroke="#E5E7EB" />
                  <text x={bx} y={by + 4} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#4B5563">{label}</text>
                </g>
              )
            })}
            {items.map((it, i) => {
              const r1 = R - i * band, r0 = r1 - thick
              const a1 = (Math.max(0, it.value) / top) * SWEEP
              const lit = hover === null || hover === i
              const rank = [...items].sort((x, y) => y.value - x.value).indexOf(it)
              const opacity = 1 - Math.min(0.45, rank * 0.09)
              return (
                <g key={i}>
                  <path d={sector(cx, cy, r0, r1, 0, SWEEP)} fill="#F1F0EC" />
                  {a1 > 0 && (
                    <path d={sector(cx, cy, r0, r1, 0, Math.max(0.02, a1))} fill={color} fillOpacity={lit ? opacity : 0.25}
                          style={{ transition: 'fill-opacity .2s', cursor: 'pointer', outline: 'none' }} tabIndex={0}
                          aria-label={`${it.name}: ${fmt(it.value)}`}
                          onMouseMove={e => { setHover(i); show(e, it) }} onFocus={e => { setHover(i); show(e, it) }}
                          onBlur={() => { setHover(null); tip.hide() }} />
                  )}
                  {/* The name and figure, at the ring's start, in the empty quarter. */}
                  <text x={cx - 8} y={cy - (r0 + r1) / 2 + 4} textAnchor="end" fontSize={Math.min(12, thick * 0.75 + 3)} fill="#1F2937" pointerEvents="none">
                    <tspan fontWeight="700">{fitText(it.name, R * 0.75, 12, true)}</tspan>
                    <tspan fill="#6B7280">{`  ${fmt(it.value)}`}</tspan>
                  </text>
                </g>
              )
            })}
          </svg>
        )}
        <ChartTip state={tip.state} />
      </div>
    </div>
  )
}
